import _ from 'lodash';
import {createSelector} from 'reselect';

import {isDemoInbox, teammateCanAccessInbox} from '../../helpers/modelHelpers';
import {ChannelModel} from '../../models/api/channelModel';
import {ConversationModel} from '../../models/api/conversationModel';
import {InboxModel} from '../../models/api/inboxModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {AppState} from '../appState';
import {GetShouldPersist} from '../appTypes';
import {getInboxes} from './inboxes/inboxesSelectors';
import {getUser, getUserId} from './teammates/teammatesSelectors';

/*
 * Selectors.
 */

export const getShouldPersistInbox: GetShouldPersist<InboxModel> = createSelector(
  [makeGetResource<InboxModel>(), getUser],
  (inbox, user) => shouldPersistInbox(inbox, user),
);

export const getShouldPersistChannel: GetShouldPersist<ChannelModel> = createSelector(
  [makeGetResource<ChannelModel>(), getInboxes, getUser],
  (channel, inboxes, user) => {
    const inbox = inboxes[channel.inboxId];
    if (!inbox) {
      return false;
    }

    return shouldPersistInbox(inbox, user);
  },
);

export const getShouldPersistConversation: GetShouldPersist<ConversationModel> = createSelector(
  [makeGetResource<ConversationModel>(), getUserId],
  (conversation, userId) => shouldPersistConversation(conversation, userId),
);

export const getShouldPersistTeammate: GetShouldPersist<TeammateModel> = createSelector(
  [makeGetResource<TeammateModel>()],
  (teammate) => shouldPersistTeammate(teammate),
);

/*
 * Helpers.
 */

function makeGetResource<T>() {
  return (_state: AppState, props: {resource: T}) => props.resource;
}

/* Whether we should persist an inbox. In the future, we will make this stricter and only keep inboxes in P1 instead of the ones we can access. */
export function shouldPersistInbox(inbox: InboxModel, user: TeammateModel): boolean {
  if (inbox.namespace === user.namespace) {
    // Keep inboxes owned by the user.
    return true;
  }

  if (teammateCanAccessInbox(user, inbox)) {
    // Keep inboxes that can be accessed by the user.
    return true;
  }

  if (isDemoInbox(inbox)) {
    // Keep demo inboxes.
    return true;
  }

  return false;
}

/* Whether we should persist a conversation. */
export function shouldPersistConversation(conversation: ConversationModel, userId: number): boolean {
  // The conversation should be persisted if the user is a tracker.
  return _(conversation.trackers)
    .values()
    .compact()
    .some((tracker) => tracker.teammateId === userId);
}

/* Whether we should persist a teammate. */
export function shouldPersistTeammate(teammate: TeammateModel): boolean {
  return false;
}
